import { Box, Skeleton, Typography } from '@mui/material';
import type { FC } from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react-lite';
import { TaskAltOutlined as TaskAltOutlinedIcon } from '@mui/icons-material';
import { AddTaskButton, DeleteTaskButton, DuplicateTaskButton, LabelFormSection } from './index';
import type { RuleTaskEntry, TaskIndexProps } from '../types';
import RuleEditVariantsOnTask from './variants/RuleEditVariantsOnTask';
import useCurrentTasksFormStore from '../hooks/useCurrentTasksFormStore';
import useRulePageMode from '../hooks/useRulePageMode';
import ConditionsGroup from './conditions/ConditionsGroup';
import ActionsGroup from './actions/ActionsGroup';

const TaskItem: FC<TaskIndexProps & { task: RuleTaskEntry }> = observer(({ task, indexTask }) => {
  const { isRead } = useRulePageMode();

  return (
    <Box
      id={`task-item-${indexTask}`}
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          '.visibleByHover': { visibility: 'hidden' },
          '&:hover .visibleByHover': { visibility: 'visible' },
          '&:last-of-type .taskGroupsContainer': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: isRead ? 'start' : 'space-between',
            minHeight: 62,
            borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,

            background: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 4,
          }}
        >
          <LabelFormSection label={`Task ${indexTask + 1}`} icon={<TaskAltOutlinedIcon />} />
          {!isRead && (
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'start', gap: 1.5 }}>
              <DuplicateTaskButton indexTask={indexTask} />
              <DeleteTaskButton indexTask={indexTask} />
            </Box>
          )}
        </Box>
        <Box
          className={'taskGroupsContainer'}
          sx={{ display: 'flex', width: '100%', py: 3, gap: 3, borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}` }}
        >
          <ConditionsGroup condition={task.condition} index={indexTask} />
          <ActionsGroup indexVariant={undefined} actions={task.actions} indexTask={indexTask} />
        </Box>
      </Box>
      <Box>
        <RuleEditVariantsOnTask indexTask={indexTask} />
      </Box>
    </Box>
  );
});

const TaskItemSkeleton: FC = () => {
  return (
    <Box
      id={`TaskItemSkeleton:task-item`}
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          '.visibleByHover': { visibility: 'hidden' },
          '&:hover .visibleByHover': { visibility: 'visible' },
          '&:last-of-type .taskGroupsContainer': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 62,
            borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,

            background: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 4,
          }}
        >
          <Skeleton width={180} height={32} sx={{ transform: 'translateY(0)', borderRadius: '8px', m: 0 }} />
          <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'start', gap: 1.5 }}>
            <Skeleton width={32} height={32} sx={{ transform: 'translateY(0)', borderRadius: '8px', m: 0 }} />
            <Skeleton width={32} height={32} sx={{ transform: 'translateY(0)', borderRadius: '8px', m: 0 }} />
          </Box>
        </Box>
        <Box
          className={'taskGroupsContainer'}
          sx={{ display: 'flex', width: '100%', py: 3, gap: 3, borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}` }}
        >
          <Skeleton width={'100%'} height={32} sx={{ transform: 'translateY(0)', borderRadius: '8px', m: 0 }} />
        </Box>
      </Box>
    </Box>
  );
};

const RuleEditTasksForm: FC = observer(() => {
  const { isRead } = useRulePageMode();
  const tasksFormStore = useCurrentTasksFormStore();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'start' }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Tasks
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          width: '100%',
          flexDirection: 'column',
        }}
      >
        {tasksFormStore.tasksForm.map((task: RuleTaskEntry, i: number) => (
          <Suspense key={`TaskItem:${i}`} fallback={<TaskItemSkeleton />}>
            <TaskItem task={task} indexTask={i} />
          </Suspense>
        ))}
      </Box>
      {!isRead && (
        <Box sx={{ display: 'flex', width: '100%', borderBottom: `1px solid ${`rgba(0, 8, 46, 0.12)`}`, py: 2 }}>
          <AddTaskButton />
        </Box>
      )}
    </Box>
  );
});

export default RuleEditTasksForm;
